import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  LockLockableInput,
  lockLockableMutation,
  lockLockableMutation$data,
} from './__generated__/lockLockableMutation.graphql'

type IssueMutationProps = {
  environment: Environment
  input: LockLockableInput
  onError?: (error: Error) => void
  onCompleted?: (response: lockLockableMutation$data) => void
}

export function commitLockLockableMutation({
  environment,
  input: {lockableId, lockReason},
  onError,
  onCompleted,
}: IssueMutationProps) {
  const inputHash: LockLockableInput = {
    lockableId,
    lockReason,
  }

  const optimistic = publishOptimisticFields(lockableId, {locked: true})

  let disposable
  try {
    disposable = commitMutation<lockLockableMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation lockLockableMutation($input: LockLockableInput!) @raw_response_type {
          lockLockable(input: $input) {
            lockedRecord {
              ... on Issue {
                locked
              }
            }
          }
        }
      `,
      variables: {
        input: inputHash,
      },
      optimisticResponse: {
        lockLockable: {
          lockedRecord: {
            __typename: 'Issue',
            __isNode: 'Issue',
            id: lockableId,
            locked: true,
          },
        },
      },
      onError: error => {
        optimistic.failed(error)
        if (onError) onError(error)
      },
      onUnsubscribe: () => optimistic.failed(new Error('Locking the conversation was cancelled')),
      onCompleted: response => {
        const locked = response.lockLockable?.lockedRecord?.locked
        if (locked == null) {
          // One error for both: the caller surfaces this through onError, so settling the
          // operation without it would make a server-side failure silent.
          const error = new Error('Failed to lock the conversation')
          optimistic.failed(error)
          if (onError) onError(error)
          return
        }
        optimistic.completed({locked})
        if (onCompleted) onCompleted(response)
      },
    })
  } catch (error) {
    optimistic.failed(error instanceof Error ? error : new Error('Failed to lock the conversation'))
    throw error
  }

  return disposable
}
